/**
 * Vizor — the Claude-powered concierge, floating on every ELF-CIRCLE page.
 *
 * The robot head already on the site is Vizor's face, as the Company Overview
 * suggests: the brand's digital presence gets a character it already owns
 * rather than a generic chat bubble.
 *
 * Without ANTHROPIC_API_KEY on the server it runs as a labelled preview: the
 * button, panel and conversation flow are all real, but replies come from a
 * short sample script instead of the model, so the design can be shown before
 * the key and API credit are in place. With the key set it is live. The conversation lives in
 * sessionStorage: every page mounts its own shell, and a visitor following a
 * link Vizor gave them should arrive with the chat still open and intact.
 *
 * Replies stream in over Server-Sent Events. Model output is only ever
 * rendered as text — paths to real site pages and http(s) URLs become links,
 * and nothing the model writes is injected as HTML.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiHeaders, config } from '../../../auth/config';
import { route } from '../../../../shared/utils';
import { ELF_NAV } from './ElfPageNav';
import vizorFace from '../../../../assets/elf/robo-head-3d.png';

interface Turn {
  role: 'user' | 'assistant';
  content: string;
}

interface Stored {
  turns: Turn[];
  open: boolean;
}

const STORE_KEY = 'elf.vizor.v1';
/** Mirrors LIMITS.userChars on the server. */
const MAX_CHARS = 2000;

const GREETING =
  "I'm Vizor, ELF-CIRCLE's concierge. Tell me what you're working on and I'll point you the right way, or book you a call with the team.";

/** One opener per client type the Company Overview names. */
const STARTERS = ['Marketing for a release', 'I need a website built', 'Sponsoring an event', 'Merch and the shop'];

const TOOL_LABELS: Record<string, string> = {
  get_available_slots: 'Checking the calendar…',
  book_consultation: 'Booking your time…',
  capture_lead: 'Passing this to the team…',
  escalate_to_human: 'Getting you a person…',
};

/** Only paths that are real pages become links — never a guessed 404. */
const SITE_PATHS = new Set(ELF_NAV.map((item) => item.to));
const TRAILING = /[.,!?;:)]+$/;
const EXTERNAL = /^https?:\/\/\S+$/i;

/** Appended to every preview reply, so a visitor never mistakes it for a live answer. */
const PREVIEW_NOTE =
  '\n\n(Preview — this is a sample reply. Live answers switch on soon; the Book a call button reaches the team today.)';

/**
 * Sample replies for preview mode. Facts are the Company Overview's, the
 * same ones the live system prompt uses, and each shows a behaviour the live
 * concierge has: pointing to a page, or checking the calendar first.
 */
function previewReply(text: string): { tool?: string; text: string } {
  const t = text.toLowerCase();
  if (/(sponsor|partner|festival|event)/.test(t)) {
    return {
      text:
        'ELF-CIRCLE owns two properties partners can back: the Made In New Jersey Festival, a statewide 21-county Arts & Culture initiative, and a monthly networking series across our launch markets. Packages are always handled by a person, and you can compare both tracks here:\n\n/sponsorship' +
        PREVIEW_NOTE,
    };
  }
  if (/(web|site|build|app|develop)/.test(t)) {
    return {
      text:
        'ELF-CIRCLE builds in house, from landing pages to enterprise sites, e-commerce, and booking or scheduler integrations, with GEO so you show up inside AI answers as well as search. What are you looking to build, and roughly when do you need it live?\n\n/services' +
        PREVIEW_NOTE,
    };
  }
  if (/(merch|shop|hoodie|tee|store)/.test(t)) {
    return {
      text:
        'Merch drops are cut for the rooms they belong to — the monthly series and the Made In New Jersey Festival. Online checkout is opening soon; you can see the current drop and register interest here:\n\n/shop' +
        PREVIEW_NOTE,
    };
  }
  if (/(book|call|time|schedule|meet|consult|slot)/.test(t)) {
    return {
      tool: 'get_available_slots',
      text:
        'I check the live calendar before offering anything. Thursday has 10:00, 11:30 and 14:00 open — pick one and I will confirm it with your name and email.' +
        PREVIEW_NOTE,
    };
  }
  if (/(market|release|album|single|brand|campaign|launch|music|artist)/.test(t)) {
    return {
      text:
        'Release marketing is core ELF-CIRCLE work: pre-release buzz, drop-day activation, playlist and press placement, and the media buy behind it, for labels and independent artists alike. When is the release, and is it label-side or independent?\n\n/services' +
        PREVIEW_NOTE,
    };
  }
  return {
    text:
      "I'm Vizor, ELF-CIRCLE's concierge. I can walk you through marketing and web-development services, the Made In New Jersey Festival and monthly events, sponsorship, or book you a call with the team. Where would you like to start?" +
      PREVIEW_NOTE,
  };
}

/** A delay the visitor can cancel by closing the chat or starting over. */
function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = window.setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(id);
        reject(new Error('aborted'));
      },
      { once: true },
    );
  });
}

/** Status is fetched once per page load, not once per page. */
let enabledCache: boolean | null = null;

function readStore(): Stored {
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return { turns: [], open: false };
    const parsed = JSON.parse(raw) as Partial<Stored>;
    const turns = Array.isArray(parsed.turns)
      ? parsed.turns.filter(
          (t): t is Turn => !!t && (t.role === 'user' || t.role === 'assistant') && typeof t.content === 'string',
        )
      : [];
    return { turns, open: parsed.open === true };
  } catch {
    return { turns: [], open: false };
  }
}

function writeStore(stored: Stored): void {
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(stored));
  } catch {
    // Private browsing or storage disabled — the chat still works, it just won't survive navigation.
  }
}

function renderText(text: string): React.ReactNode {
  return text.split(/\n{2,}/).map((para, p) => (
    <p key={p}>
      {para.split('\n').map((line, l) => (
        <React.Fragment key={l}>
          {l > 0 && <br />}
          {line.split(/(\s+)/).map((token, t) => {
            const core = token.replace(TRAILING, '');
            const tail = token.slice(core.length);
            if (EXTERNAL.test(core)) {
              return (
                <React.Fragment key={t}>
                  <a href={core} target="_blank" rel="noopener noreferrer">
                    {core}
                  </a>
                  {tail}
                </React.Fragment>
              );
            }
            if (SITE_PATHS.has(core)) {
              return (
                <React.Fragment key={t}>
                  <Link to={route(core)}>{core}</Link>
                  {tail}
                </React.Fragment>
              );
            }
            return <React.Fragment key={t}>{token}</React.Fragment>;
          })}
        </React.Fragment>
      ))}
    </p>
  ));
}

/** Parses a text/event-stream response body into `{ event, data }` pairs. */
async function* readEvents(res: Response): AsyncGenerator<{ event: string; data: unknown }> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let cut = buffer.indexOf('\n\n');
    while (cut !== -1) {
      const block = buffer.slice(0, cut);
      buffer = buffer.slice(cut + 2);
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) event = line.slice(7);
        else if (line.startsWith('data: ')) data += line.slice(6);
      }
      if (data) {
        try {
          yield { event, data: JSON.parse(data) };
        } catch {
          // A malformed frame is skipped rather than ending the reply.
        }
      }
      cut = buffer.indexOf('\n\n');
    }
  }
}

export const ElfVizor: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(enabledCache);
  const [{ turns, open }, setStored] = useState<Stored>(readStore);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolNote, setToolNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  /** Server reachable but no key (or status unreachable): run the sample script. */
  const preview = enabled === false;

  useEffect(() => writeStore({ turns, open }), [turns, open]);

  useEffect(() => {
    if (enabledCache !== null) return;
    let cancelled = false;
    fetch(`${config.api.baseUrl}/api/vizor/status`, { headers: apiHeaders() })
      .then((res) => (res.ok ? res.json() : { enabled: false }))
      .then((body: { enabled?: boolean }) => {
        enabledCache = body.enabled === true;
        if (!cancelled) setEnabled(enabledCache);
      })
      .catch(() => {
        if (!cancelled) setEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Leaving the page mid-reply stops the model call instead of paying for text nobody reads.
  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [turns, toolNote, error, open]);

  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setStored((s) => ({ ...s, open: false }));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || busy) return;
      setError(null);
      setDraft('');
      const history: Turn[] = [...turns, { role: 'user', content }];
      setStored((s) => ({ ...s, turns: [...history, { role: 'assistant', content: '' }] }));
      setBusy(true);
      const controller = new AbortController();
      abortRef.current = controller;

      const appendDelta = (delta: string) =>
        setStored((s) => {
          const next = s.turns.slice();
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + delta };
          return { ...s, turns: next };
        });

      if (preview) {
        try {
          await wait(650, controller.signal);
          const reply = previewReply(content);
          if (reply.tool) {
            setToolNote(TOOL_LABELS[reply.tool] ?? 'Working on it…');
            await wait(1100, controller.signal);
            setToolNote(null);
          }
          // Reveal in small chunks so the preview shows the streaming style.
          for (const chunk of reply.text.match(/[\s\S]{1,5}/g) ?? []) {
            appendDelta(chunk);
            await wait(16, controller.signal);
          }
        } catch {
          // Closed or restarted mid-reply — nothing to report.
        } finally {
          setStored((st) => {
            const last = st.turns[st.turns.length - 1];
            return last && last.role === 'assistant' && !last.content ? { ...st, turns: st.turns.slice(0, -1) } : st;
          });
          setToolNote(null);
          setBusy(false);
          if (abortRef.current === controller) abortRef.current = null;
        }
        return;
      }

      try {
        const res = await fetch(`${config.api.baseUrl}/api/vizor/chat`, {
          method: 'POST',
          headers: apiHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({
            messages: history.filter((t) => t.content),
            pageUrl: window.location.pathname,
          }),
          signal: controller.signal,
        });
        if (!res.ok || !res.body) {
          const body = (await res.json().catch(() => null)) as { message?: string } | null;
          throw new Error(
            body?.message ??
              (res.status === 429
                ? "You're sending messages quickly. Give it a moment and try again."
                : 'Vizor is unavailable right now. The Book a call button still reaches the team.'),
          );
        }
        for await (const { event, data } of readEvents(res)) {
          const payload = data as { delta?: string; name?: string; message?: string };
          if (event === 'text' && payload.delta) {
            setToolNote(null);
            appendDelta(payload.delta);
          } else if (event === 'tool' && payload.name) {
            setToolNote(TOOL_LABELS[payload.name] ?? 'Working on it…');
          } else if (event === 'error') {
            throw new Error(payload.message ?? 'Something went wrong.');
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      } finally {
        // A reply that never received any text is dropped, so a failure leaves
        // the error message rather than an empty bubble.
        setStored((s) => {
          const last = s.turns[s.turns.length - 1];
          return last && last.role === 'assistant' && !last.content ? { ...s, turns: s.turns.slice(0, -1) } : s;
        });
        setToolNote(null);
        setBusy(false);
        if (abortRef.current === controller) abortRef.current = null;
      }
    },
    [busy, turns, preview],
  );

  const reset = () => {
    abortRef.current?.abort();
    setStored((s) => ({ ...s, turns: [] }));
    setError(null);
    setDraft('');
  };

  if (enabled === null) return null;

  const lastTurn = turns[turns.length - 1];
  const waiting = busy && (toolNote !== null || !lastTurn?.content);

  return (
    <div className="vizor" data-open={open ? 'true' : undefined}>
      {open && (
        <section className="vizor__panel" role="dialog" aria-label="Chat with Vizor">
          <header className="vizor__head">
            <img className="vizor__face" src={vizorFace} alt="" />
            <div className="vizor__id">
              <span className="vizor__name">Vizor</span>
              <span className="vizor__role">ELF-CIRCLE concierge · {preview ? 'Preview' : 'AI'}</span>
            </div>
            {turns.length > 0 && (
              <button type="button" className="vizor__new" onClick={reset}>
                New chat
              </button>
            )}
            <button
              type="button"
              className="vizor__x"
              aria-label="Close chat"
              onClick={() => setStored((s) => ({ ...s, open: false }))}
            >
              ×
            </button>
          </header>

          <div className="vizor__log" ref={logRef} aria-live="polite">
            <div className="vizor__msg vizor__msg--bot">{renderText(GREETING)}</div>
            {turns.length === 0 && (
              <div className="vizor__starters">
                {STARTERS.map((starter) => (
                  <button key={starter} type="button" className="vizor__chip" onClick={() => void send(starter)}>
                    {starter}
                  </button>
                ))}
              </div>
            )}
            {turns.map((turn, i) =>
              turn.content ? (
                <div key={i} className={turn.role === 'user' ? 'vizor__msg vizor__msg--me' : 'vizor__msg vizor__msg--bot'}>
                  {turn.role === 'user' ? turn.content : renderText(turn.content)}
                </div>
              ) : null,
            )}
            {waiting && (
              <div className="vizor__status">
                <span className="vizor__pulse" aria-hidden="true" />
                {toolNote ?? 'Thinking…'}
              </div>
            )}
            {error && (
              <div className="vizor__error" role="alert">
                {error}
              </div>
            )}
          </div>

          <form
            className="vizor__form"
            onSubmit={(e) => {
              e.preventDefault();
              void send(draft);
            }}
          >
            <textarea
              ref={inputRef}
              className="vizor__input"
              rows={1}
              value={draft}
              maxLength={MAX_CHARS}
              placeholder="Ask about services, events, sponsorship…"
              aria-label="Message Vizor"
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send(draft);
                }
              }}
            />
            <button type="submit" className="vizor__send" disabled={busy || !draft.trim()} aria-label="Send message">
              →
            </button>
          </form>
          <p className="vizor__fine">
            {preview
              ? 'Preview — replies are samples until live answers are switched on.'
              : 'Vizor is an AI and can get things wrong. Never share payment details in chat.'}
          </p>
        </section>
      )}

      <button
        type="button"
        className="vizor__launch"
        aria-expanded={open}
        aria-label={open ? 'Close chat with Vizor' : 'Chat with Vizor'}
        onClick={() => setStored((s) => ({ ...s, open: !s.open }))}
      >
        <img src={vizorFace} alt="" />
        <span className="vizor__launch-label">Ask Vizor</span>
      </button>
    </div>
  );
};

export default ElfVizor;
